/**
 * @fileoverview EnrollmentDetailsCard — multi-section `bp6-card` on the
 * Control Panel home page surfacing the current enrollment + its default
 * domain + the list of organizations under it. Anatomy:
 *   • Title row: "Enrollment Details" header + primary-minimal "Show details"
 *   • EntityWithIcon row (diagram-tree icon + enrollment name)
 *   • Default-domain row: truncated domain + primary-minimal "Default" Tag
 *   • Divider
 *   • Organizations sub-section: section title + list of org rows
 *     (EntityWithIcon + info-sign tooltip trigger)
 *
 * Foundry mapping: `bp6-card bp6-elevation-0
 * control-panel-app__container__femj1l` + `__title` + `__default-domain`
 * + `__default-tag` + `__divider` + `__list` + `__org-row`.
 */

import { Button, Divider, Icon, Tag } from "@blueprintjs/core";
import type { IconName } from "@blueprintjs/icons";
import type { ReactElement, ReactNode } from "react";

import { EntityWithIcon } from "./entity-with-icon";
import { controlPanelApp as cpa } from "./foundry-parts.generated";

export interface EnrollmentDetailsOrg {
  id: string;
  name: string;
  icon?: IconName;
  /** Click handler for the trailing info-sign tooltip trigger. */
  onInfo?: () => void;
}

interface EnrollmentDetailsCardProps {
  enrollmentName: string;
  enrollmentIcon?: IconName;
  defaultDomain: string;
  /** Label for the "Default" tag (default: "Default"). */
  defaultDomainTagLabel?: string;
  onShowDetails?: () => void;
  organizations?: readonly EnrollmentDetailsOrg[];
  organizationsTitle?: string;
  /** Optional trailing slot inside the organizations section (e.g. a
   *  "+ Add" button or a count). */
  organizationsAction?: ReactNode;
  className?: string;
}

export function EnrollmentDetailsCard({
  enrollmentName,
  enrollmentIcon = "diagram-tree",
  defaultDomain,
  defaultDomainTagLabel = "Default",
  onShowDetails,
  organizations,
  organizationsTitle = "Organizations",
  organizationsAction,
  className,
}: EnrollmentDetailsCardProps): ReactElement {
  return (
    <div data-testid="enrollment-details-card"
      className={[
        "bp6-card bp6-elevation-0",
        "bp-enrollment-details-card",
        className,
      ].filter(Boolean).join(" ")}
    >
      {/*
        No inner landmark here. The outer div IS the card, and a region
        carrying the same "Enrollment details" name inside it is the card
        announced twice — a landmark wrapping itself. Removing it also puts
        bp6-card where the capture has it instead of one level up.
      */}
      <div>
        <div>
          <div>
            Enrollment Details
          </div>
          <Button
            minimal
            small
            intent="primary"
            text="Show details"
            onClick={onShowDetails}
          />
        </div>
        <EntityWithIcon icon={enrollmentIcon} name={enrollmentName} />
        <div>
          <div className="bp6-text-overflow-ellipsis">{defaultDomain}</div>
          <Tag minimal intent="primary">
            {defaultDomainTagLabel}
          </Tag>
        </div>
      </div>

      {organizations !== undefined && (
        <>
          <Divider />
          <section aria-label="Enrollment settings">
            <header>
              <div>
                {organizationsTitle}
              </div>
              {organizationsAction}
            </header>
            {/*
              DIVS, NOT A LIST. This was `ul > li`, which rendered a bullet
              beside every organization and stacked the name under the icon; and
              the info affordance was a `button`, which drew a box around a
              16px icon. The capture has
              `div.__list__ > div.__org-row__ > (entity-with-icon, info icon)` —
              a row, and a bare icon in a popover target.
            */}
            <div className={cpa["list"]}>
              {organizations.map((org) => (
                <div key={org.id} className={cpa["org-row"]}>
                  <EntityWithIcon icon={org.icon ?? "office"} name={org.name} />
                  <span className="bp6-popover-target">
                    <Icon
                      icon="info-sign"
                      aria-label="More information about the organization"
                      aria-haspopup="menu"
                      tabIndex={0}
                      onClick={org.onInfo}
                    />
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
